import { evaluate, toast } from "@kyan/ui";
import { useDebounceValue } from "usehooks-ts";

import { defaultUnderlying } from "#/config/markets";
import { useTradeButtonFeatureFlag } from "#/hooks/featureFlags/useTradeButtonFeatureFlag";
import { usePortfolioToastAction } from "#/hooks/usePortfolioToastActions";
import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { useValidateOptionSize } from "#/hooks/useValidateOptionSize";
import { useValidatePerpSize } from "#/hooks/useValidatePerpSize";
import { useCalculateUserRisk } from "#/services/kyan/hooks/useCalculateUserRisk";
import { usePostLimitOrders } from "#/services/kyan/hooks/usePostLimitOrders";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useEquityModalTab } from "#/store/app/equityModal/equityModalTab";
import { useShowEquityModal } from "#/store/app/equityModal/showEquityModal";
import { useMarket } from "#/store/app/portfolio/market";
import { useShowTradeConfirmation } from "#/store/app/portfolio/settings/showTradeConfirmation";
import { useTradePanelTab } from "#/store/app/tradePanel/tradePanelTab";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionIsPostOnly } from "#/store/options/tradePanel/optionIsPostOnly";
import { useOptionLimitOrderType } from "#/store/options/tradePanel/optionLimitOrderType";
import { useOptionLimitPrice } from "#/store/options/tradePanel/optionLimitPrice";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpIsPostOnly } from "#/store/perps/tradePanel/perpIsPostOnly";
import { usePerpLimitOrderType } from "#/store/perps/tradePanel/perpLimitOrderType";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { useAccount } from "#/store/wallet/account/account";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type PostLimitOrder } from "#/types/orders";
import { getMmrImr } from "#/utils/getMmrImr";
import {
  createPerpInstrumentName,
  formatInstrumentName,
} from "#/utils/instrument.utils";
import { isBuy, toDirection } from "#/utils/marketActionConverter";
import { getMarketByUnderlying } from "#/utils/marketPair";

const useOptionDetails = () => {
  const { option } = useOption();
  const { optionLimitPrice } = useOptionLimitPrice();
  const { optionSize } = useOptionSize();
  const { optionLimitOrderType } = useOptionLimitOrderType();
  const { optionIsPostOnly } = useOptionIsPostOnly();
  const { account } = useAccount();
  const underlying = option?.underlying ?? defaultUnderlying;

  // Placeholder logic before Options Updater:
  const [debouncedSize] = useDebounceValue(optionSize, 500);
  const [debouncedLimitPrice] = useDebounceValue(optionLimitPrice, 500);

  const sizeError = useValidateOptionSize({
    underlying,
    size: debouncedSize,
    isLimit: true,
  });

  const isMissingDetails =
    !debouncedSize || !debouncedLimitPrice || Boolean(sizeError);

  const optionMarket = getMarketByUnderlying(underlying);

  return {
    market: optionMarket,
    isValid: Boolean(option && account && !sizeError),
    isMissingDetails,
    orders: [
      {
        instrument_name: option?.instrument_name,
        type: optionLimitOrderType,
        contracts: Number(debouncedSize),
        direction: option?.direction,
        price: Number(debouncedLimitPrice),
        post_only: optionIsPostOnly,
        mmp: false,
        liquidation: false,
        maker: account,
      },
    ] as PostLimitOrder[],
    size: debouncedSize,
    limitPrice: debouncedLimitPrice,
    direction: option?.direction,
    sizeError,
  };
};

const usePerpsDetails = () => {
  const { market } = useMarket();
  const { perpSize } = usePerpSize();
  const { perpLimitPrice } = usePerpLimitPrice();
  const { perpLimitOrderType } = usePerpLimitOrderType();
  const { perpIsPostOnly } = usePerpIsPostOnly();
  const { perpIsBuy } = usePerpIsBuy();
  const { account } = useAccount();

  // Placeholder logic before Options Updater:
  const [debouncedSize] = useDebounceValue(perpSize, 500);
  const [debouncedLimitPrice] = useDebounceValue(perpLimitPrice, 500);

  const { hasSizeError, sizeError, hasSizeWarning } = useValidatePerpSize({
    isLimit: true,
  });

  const isMissingDetails =
    !debouncedSize || !debouncedLimitPrice || hasSizeError || hasSizeWarning;

  return {
    market,
    isValid: Boolean(account) && !hasSizeError && !hasSizeWarning,
    isMissingDetails,
    orders: [
      {
        instrument_name: createPerpInstrumentName(market.pair),
        type: perpLimitOrderType,
        contracts: Number(perpSize),
        direction: toDirection(perpIsBuy),
        price: Number(debouncedLimitPrice),
        post_only: perpIsPostOnly,
        mmp: false,
        liquidation: false,
        maker: account,
      },
    ] as PostLimitOrder[],
    size: debouncedSize,
    limitPrice: debouncedLimitPrice,
    direction: toDirection(perpIsBuy),
    sizeError,
  };
};

export const useLimitTradeButtonController = (onSuccess?: () => void) => {
  const { isPerps } = useTradePanelTab();
  const perpsDetails = usePerpsDetails();
  const { setOptionSize } = useOptionSize();
  const { setOptionLimitPrice } = useOptionLimitPrice();
  const { setPerpSizeInputValue } = usePerpSize();
  const { setPerpLimitPrice } = usePerpLimitPrice();
  const { showTradeConfirmation } = useShowTradeConfirmation();
  const { isDevMode } = useIsDevMode();
  const optionDetails = useOptionDetails();
  const { isTradeButtonEnabled, tradeButtonTitle } =
    useTradeButtonFeatureFlag();
  const { setShowEquityModal } = useShowEquityModal();
  const { setEquityModalTab } = useEquityModalTab();
  const { setEquityModalPortfolio } = useEquityModalPortfolio();
  const { refetch: refetchAccountState } = useRefetchAccountStateIfStale();
  const { getAction } = usePortfolioToastAction();

  const {
    market,
    isValid,
    isMissingDetails,
    orders,
    size,
    limitPrice,
    direction: tradeDirection,
    sizeError,
  } = isPerps ? perpsDetails : optionDetails;

  const { portfolioState } = useAccountState(market.underlying);
  const { isLiquidated, hasZeroEquity, hasTransferableEquity } =
    usePortfolioValidations(market);

  const {
    im,
    mm,
    isLoading: isLoadingRisk,
  } = useCalculateUserRisk({
    inputs: [
      {
        instrument: orders[0].instrument_name,
        size,
        limitPrice,
        isBuy: isBuy(tradeDirection!),
        isEnabled: isValid,
      },
    ],
    option: isPerps ? null : undefined,
  });

  const { imr } = getMmrImr({ equity: portfolioState.equity, mm, im });

  const hasValidImr = imr < 1;

  const label = evaluate({
    [tradeButtonTitle]: !isTradeButtonEnabled,
    "Liquidation Active": isLiquidated,
    "Invalid size": sizeError,
    "Enter missing details": isMissingDetails,
    "Insufficient equity": hasZeroEquity,
    "Insufficient Margin": !hasValidImr,
    "Submit order": isValid,
  });

  const { mutate, isPending } = usePostLimitOrders({
    onSuccess: (response: { status: string; instrument: string }) => {
      toast.success({
        title: `Limit order ${response.status}`,
        description: `For ${formatInstrumentName(response.instrument)}`,
        action: getAction({ target: "index", label: "View in Portfolio" }),
      });

      clearOrderValues();
      onSuccess?.();

      refetchAccountState?.();
    },
    onError: (error: Error) => {
      toast.error({
        title: "Error posting order",
        description: error.message,
      });
    },
  });

  const clearOrderValues = () => {
    if (isPerps) {
      setPerpSizeInputValue("");
      setPerpLimitPrice("");
      return;
    }

    setOptionSize("");
    setOptionLimitPrice("");
  };

  const isDisabled =
    !isTradeButtonEnabled ||
    (!isDevMode &&
      (isMissingDetails || hasZeroEquity || !hasValidImr || isLiquidated));
  const buttonLabel = `${label}${isDevMode ? " (Dev)" : ""}`;

  const handlePostOrder = () => {
    if (!isValid) return; // TODO: this needs to be handled in the UI

    mutate(orders);
  };

  const isLoading = isPending || isLoadingRisk;

  const handleSecondaryButtonClick = () => {
    setShowEquityModal(true);
    setEquityModalPortfolio(market.underlying);
    setEquityModalTab(hasTransferableEquity ? "transfer" : "deposit");
  };

  const secondaryButtonLabel = hasTransferableEquity
    ? "Transfer/deposit more USDC"
    : "Deposit more USDC";

  const isSecondaryButtonVisible = hasZeroEquity || !hasValidImr;

  return {
    isPerps,
    isLoading,
    buttonLabel,
    isDisabled,
    isSecondaryButtonVisible,
    secondaryButtonLabel,
    showTradeConfirmation,
    handlePostOrder,
    handleSecondaryButtonClick,
  };
};
